import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const ConversationUI = ({
  messages,
  onSendMessage,
  onCorrect,
  loading,
  progress,
  extractedContent,
  disabled
}) => {
  const [inputValue, setInputValue] = useState('');
  const [correctionMode, setCorrectionMode] = useState(null);
  const [correctionValue, setCorrectionValue] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!loading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [loading]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim() || loading || disabled) return;

    onSendMessage(inputValue.trim());
    setInputValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleCorrection = () => {
    if (!correctionMode || !correctionValue.trim()) return;
    onCorrect(correctionMode.field, correctionValue.trim());
    setCorrectionMode(null);
    setCorrectionValue('');
  };

  const renderMessage = (message, index) => {
    const isUser = message.role === 'user';
    const isSystem = message.role === 'system';

    if (isSystem) {
      return (
        <div key={message.id || index} className="flex justify-center my-2">
          <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {message.content}
          </span>
        </div>
      );
    }

    return (
      <div
        key={message.id || index}
        className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
      >
        <div
          className={`max-w-[80%] rounded-2xl px-4 py-3 ${
            isUser
              ? 'bg-primary-600 text-white rounded-br-md'
              : 'bg-gray-100 text-gray-900 rounded-bl-md'
          }`}
        >
          <div className="whitespace-pre-wrap text-sm leading-relaxed">
            {message.content.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className={idx > 0 ? 'mt-3' : ''}>
                {paragraph.startsWith('**') && paragraph.endsWith('**') ? (
                  <strong>{paragraph.slice(2, -2)}</strong>
                ) : (
                  paragraph
                )}
              </p>
            ))}
          </div>

          {!isUser && message.questionType && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <span className="text-xs text-gray-500 capitalize">
                {message.questionType.replace('_', ' ')}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Progress bar */}
      {progress && (
        <div className="px-4 py-2 bg-white border-b">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
            <span>Progress</span>
            <span>{progress.current} of {progress.total}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <p>Starting your conversation...</p>
          </div>
        ) : (
          <>
            {messages.map(renderMessage)}

            {loading && (
              <div className="flex justify-start mb-4">
                <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-sm text-gray-500">Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Correction mode */}
      {correctionMode && (
        <div className="px-4 py-3 bg-amber-50 border-t border-amber-200">
          <div className="flex items-start gap-2 mb-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">
                Correcting: {correctionMode.label}
              </p>
              <p className="text-xs text-amber-600">
                Original: {correctionMode.original}
              </p>
            </div>
            <button
              onClick={() => {
                setCorrectionMode(null);
                setCorrectionValue('');
              }}
              className="text-amber-600 hover:text-amber-800"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={correctionValue}
              onChange={(e) => setCorrectionValue(e.target.value)}
              placeholder="Enter the correct value..."
              className="flex-1 px-3 py-2 text-sm border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              autoFocus
            />
            <button
              onClick={handleCorrection}
              disabled={!correctionValue.trim()}
              className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="px-4 py-3 bg-white border-t">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={disabled ? 'Conversation complete' : 'Type your response...'}
            disabled={loading || disabled}
            rows={1}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            style={{ minHeight: '44px', maxHeight: '120px' }}
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || loading || disabled}
            className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <PaperAirplaneIcon className="w-5 h-5" />
          </button>
        </form>

        {!disabled && (
          <div className="mt-2 flex justify-center">
            <button
              type="button"
              onClick={() => setCorrectionMode({
                field: 'general',
                label: 'Something I said',
                original: 'Click to specify what needs correction'
              })}
              className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
            >
              That's not quite right? Click to correct.
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

ConversationUI.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      role: PropTypes.oneOf(['system', 'assistant', 'user']).isRequired,
      content: PropTypes.string.isRequired,
      questionType: PropTypes.string
    })
  ).isRequired,
  onSendMessage: PropTypes.func.isRequired,
  onCorrect: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  progress: PropTypes.shape({
    current: PropTypes.number,
    total: PropTypes.number
  }),
  extractedContent: PropTypes.object,
  disabled: PropTypes.bool
};

export default ConversationUI;
